import React from "react";
import { useForm, FormProvider } from "react-hook-form";
import { SimpleGrid, Box, Heading, Flex, Link } from "@chakra-ui/react";
import Signin from "@/components/Signin";
import UserForm from "@/components/Signup/UserForm";
import { useTranslation } from "next-i18next";
import axios from "axios";
import BoxCard from "@/components/common/BoxCard";
import { useToastPromise } from "@/hooks/useToast";
import { useRouter } from "next/router";
import { Image } from "@/components/common/Image";

type Props = {
  providers: any;
  eventId?: string | string[];
};

const SigninPage = ({ providers, eventId }: Props): React.ReactElement => {
  const { t } = useTranslation("common");
  const toast = useToastPromise();
  const { locale } = useRouter();
  const methods = useForm();
  const { handleSubmit, reset } = methods;

  const onSubmit = (data) => {
    return toast.promise(
      axios.post("/user", { ...data, eventId, locale }).then(() => {
        reset();
      }),
      {
        success: t("users.accountCreated"),
        errorKeys: {
          "Email taken": t("users.emailTaken"),
          OfficeRequestNotExists: t("errorKeys.officeRequestNotExists"),
        },
      }
    );
  };

  return (
    <SimpleGrid columns={{ base: 1, md: 2 }} spacing={6}>
      <BoxCard p={0} rounded={false}>
        <Flex
          p="1.25rem"
          borderBottomColor="light.50"
          borderBottomWidth="1px"
          align={{ base: "flex-start", md: "center" }}
          direction={{ base: "column", md: "row" }}
        >
          <Heading as="h3" size="md" mb="0">
            {t("users.signinNounWith")}
          </Heading>
          <Link isExternal href="https://accred.eu">
            <Box
              width="116px"
              height="28px"
              position="relative"
              ml={{ base: 0, md: 3 }}
              mt={{ base: 3, md: 0 }}
            >
              <Image
                priority
                src="/logo-blue.svg"
                objectFit="contain"
                objectPosition="left"
                alt=""
              />
            </Box>
          </Link>
        </Flex>
        <Box p="1.25rem">
          <Signin providers={providers} page />
        </Box>
      </BoxCard>
      <BoxCard p={0} rounded={false}>
        <Box p="1.25rem" borderBottomColor="light.50" borderBottomWidth="1px">
          <Heading as="h3" size="md" mb="0">
            {t("users.signupNoun")}
          </Heading>
        </Box>
        <Box p="1.25rem">
          <FormProvider {...methods}>
            <form onSubmit={handleSubmit(onSubmit)} noValidate>
              <UserForm page />
            </form>
          </FormProvider>
        </Box>
      </BoxCard>
    </SimpleGrid>
  );
};

export default SigninPage;
